import {propertyBounds} from './propertyFocus';
import {insideProperty} from '../config/gpsGeometry.js';
import {useEffect,useRef,useState} from 'react';
import L from 'leaflet';
import {useGpsSession} from './GpsSessionContext';
import {workspaceAccount} from './db';
interface Options {map:L.Map|null;geofenceGeoJson?:unknown;enabledByRole:boolean;lastKnownTtlMs?:number;followPreference?:boolean;zoomIncrease?:number;inspectingRoute?:boolean;viewResetToken?:number;routeViewId?:string|null}
export const useGpsTracking=({map,geofenceGeoJson,enabledByRole,lastKnownTtlMs=240*60000,followPreference=true,zoomIncrease=2,inspectingRoute=false,viewResetToken=0,routeViewId}:Options)=>{
 const {enabled,state,start,stop,foregroundEpoch}=useGpsSession();
 const key='gps:map-paused:'+workspaceAccount;
 const readPaused=()=>{try{return sessionStorage.getItem(key)==='true';}catch{return false;}};
 const [followMe,setFollowMe]=useState(followPreference&&!readPaused()),[now,setNow]=useState(Date.now());
 const [recenterToLocation,setRecenterToLocation]=useState(false);
 const programmatic=useRef(false),focused=useRef(false),epoch=useRef(foregroundEpoch),foregroundAt=useRef(0);
 const inspecting=useRef(inspectingRoute);inspecting.current=inspectingRoute;
 const restoredToken=useRef(0);
 useEffect(()=>{if(!routeViewId)return;focused.current=false;setFollowMe(followPreference);try{sessionStorage.removeItem(key);}catch{}},[routeViewId,followPreference]);
 const pause=()=>{setFollowMe(false);try{sessionStorage.setItem(key,'true');}catch{}};
 useEffect(()=>{if(epoch.current!==foregroundEpoch){epoch.current=foregroundEpoch;foregroundAt.current=Date.now();focused.current=false;setFollowMe(followPreference);try{sessionStorage.removeItem(key);}catch{}}},[foregroundEpoch,followPreference]);
 useEffect(()=>{setFollowMe(followPreference&&!readPaused());},[followPreference]);
 useEffect(()=>{const timer=setInterval(()=>setNow(Date.now()),5000);return()=>clearInterval(timer);},[]);
 const lastFix=enabled&&state.position&&now-state.position.timestamp<lastKnownTtlMs?state.position:null;
 const inside=!!lastFix&&insideProperty(geofenceGeoJson,lastFix.coords.latitude,lastFix.coords.longitude);
 const fresh=!!lastFix&&state.status==='tracking'&&Date.now()-lastFix.timestamp<45000&&lastFix.timestamp>=foregroundAt.current;
 const nearProperty=enabledByRole&&enabled&&inside&&fresh;
 useEffect(()=>{if(!nearProperty)setRecenterToLocation(false);},[nearProperty]);
 const setView=(zoom:boolean)=>{
  if(!map||!lastFix||!nearProperty)return;
  programmatic.current=true;
  const bounds=propertyBounds(geofenceGeoJson);
  const baseline=bounds?map.getBoundsZoom(L.latLngBounds(bounds),false,L.point(40,40)):map.getZoom();
  const level=Math.max(map.getMinZoom(),Math.min(map.getMaxZoom(),baseline+zoomIncrease));
  map.setView([lastFix.coords.latitude,lastFix.coords.longitude],zoom?level:map.getZoom(),{animate:false});programmatic.current=false;
 };
 useEffect(()=>{
  if(!followMe||!nearProperty||!map||!lastFix||inspectingRoute)return;
  setView(!focused.current);focused.current=true;
 },[followMe,nearProperty,map,lastFix,geofenceGeoJson,zoomIncrease,inspectingRoute,foregroundEpoch,routeViewId]);
 useEffect(()=>{
  if(!map)return;
  const manual=()=>{if(!programmatic.current&&!inspecting.current)pause();};
  const fitStart=()=>{programmatic.current=true;},fitEnd=()=>{programmatic.current=false;};
  map.on('routefitstart',fitStart);map.on('routefitend',fitEnd);map.on('dragstart',manual);map.on('zoomstart',manual);return()=>{map.off('routefitstart',fitStart);map.off('routefitend',fitEnd);map.off('dragstart',manual);map.off('zoomstart',manual);};
 },[map,key]);
 const canRecenter=!!map&&(nearProperty||!!propertyBounds(geofenceGeoJson));
 const fitProperty=()=>{
  const bounds=propertyBounds(geofenceGeoJson);
  if(!map||!bounds)return false;
  programmatic.current=true;map.fitBounds(bounds,{padding:[20,20],maxZoom:18,animate:false});programmatic.current=false;
  return true;
 };
 const recenter=(resumeFollowing=true,preferLocation=true)=>{
  if(!map||!resumeFollowing)return;
  if(nearProperty&&preferLocation)setView(true);else fitProperty();
  setRecenterToLocation(false);
  try{sessionStorage.removeItem(key);}catch{}setFollowMe(true);focused.current=nearProperty&&preferLocation;
 };
 // Explicit button presses alternate; route/entry resets still use recenter.
 const recenterNext=()=>{
  if(!canRecenter)return;
  if(!nearProperty||recenterToLocation){recenter();return;}
  if(fitProperty()){
   pause();focused.current=false;setRecenterToLocation(true);
  }
 };
 const recenterLabel=nearProperty&&recenterToLocation?'Recenter on my location':'Fit property to map';
 useEffect(()=>{
  if(!viewResetToken||viewResetToken===restoredToken.current||!map||inspectingRoute||!propertyBounds(geofenceGeoJson))return;
  restoredToken.current=viewResetToken;recenter(true,followPreference);if(!followPreference)setFollowMe(false);
 },[viewResetToken,map,inspectingRoute,geofenceGeoJson,followPreference]);
 return {enabled,permissionState:state.permission,trackingState:state.status,lastFix,currentHeadingDeg:lastFix?.coords.heading??null,
  inGeofence:lastFix?inside:null,nearProperty,canRecenter,followMe,errors:state.error?[state.error]:[],lastSuccessfulFixTs:lastFix?.timestamp??null,
  start,stop,recenter,recenterNext,recenterLabel,toggleFollow:()=>{if(followMe)pause();else recenter();}};
};
